import { useState, useMemo, useEffect } from 'react';
import type { Contract, Harvest, Season, Limit, AdvanceRequest, EditHistory } from '../../../../types';
import type { FarmerKycInfo } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { formatVND, formatDate, formatDateTime, formatNumber } from '../../../../utils/format';
import { calculateLimitProposal, getRiskRatioTable } from '../../../../utils/limitProposal';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { getFileDownloadUrl } from '../../../../api/file';

interface ContractDetailModalProps {
  contract: Contract;
  farmerName: string;
  farmerId: string;
  farmerKyc: FarmerKycInfo;
  seasonNames: string[];
  seasons: Season[];
  relatedHarvests: Harvest[];
  relatedLimits: Limit[];
  relatedLoans: AdvanceRequest[];
  editHistories: EditHistory[];
  riskMucDo?: string;
  riskMoTa?: string;
  onClose: () => void;
  onEdit?: () => void;
  onAddDocs?: () => void;
  onExtend?: () => void;
  onViewRisk?: () => void;
}

type TabKey = 'info' | 'seasons' | 'limits' | 'loans' | 'docs' | 'history';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'info', label: 'Thông tin', icon: 'ri-file-list-line' },
  { key: 'seasons', label: 'Mùa vụ', icon: 'ri-seedling-line' },
  { key: 'limits', label: 'Hạn mức', icon: 'ri-bank-card-line' },
  { key: 'loans', label: 'Khoản vay', icon: 'ri-money-cny-circle-line' },
  { key: 'docs', label: 'Tài liệu', icon: 'ri-attachment-line' },
  { key: 'history', label: 'Lịch sử', icon: 'ri-history-line' },
];

function getFileIcon(type: 'image' | 'doc'): string {
  return type === 'image' ? 'ri-image-line' : 'ri-file-pdf-line';
}

export default function ContractDetailModal({
  contract,
  farmerName,
  farmerId,
  farmerKyc,
  seasonNames,
  seasons,
  relatedHarvests,
  relatedLimits,
  relatedLoans,
  editHistories,
  riskMucDo,
  riskMoTa,
  onClose,
  onEdit,
  onAddDocs,
  onExtend,
  onViewRisk,
}: ContractDetailModalProps) {
  useModalBackHandler(true, onClose);
  const [activeTab, setActiveTab] = useState<TabKey>('info');
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    (contract.attachments || []).forEach((att) => {
      if (docUrls[att.fileId]) return;
      getFileDownloadUrl(att.fileId)
        .then((url) => setDocUrls((prev) => ({ ...prev, [att.fileId]: url })))
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.attachments]);

  const isBaoTieu = contract.loaiHopDong === 'HTX-Nông hộ';
  const docCount = contract.attachments?.length || 0;
  const hanMucDaCap = contract.hanMucDaCap || 0;
  const hanMucConLai = contract.hanMucConLai ?? (contract.giaTri - hanMucDaCap);
  const usedPercent = contract.giaTri > 0 ? Math.round((hanMucDaCap / contract.giaTri) * 100) : 0;

  // Limit proposal
  const proposal = useMemo(() => {
    if (!isBaoTieu || contract.giaTri <= 0) return null;
    return calculateLimitProposal(
      contract.giaTri,
      farmerKyc.riskLevel || 'Chưa tính',
      hanMucDaCap,
      farmerKyc.creditScore,
    );
  }, [isBaoTieu, contract.giaTri, farmerKyc.riskLevel, farmerKyc.creditScore, hanMucDaCap]);

  // Warnings
  const warnings = useMemo(() => {
    const w: string[] = [];
    const today = new Date();

    if (!contract.attachments || contract.attachments.length === 0) {
      w.push('Chưa có tài liệu đính kèm');
    }

    if (contract.ngayHetHan) {
      const d = new Date(contract.ngayHetHan);
      const diff = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diff <= 15 && diff >= 0) w.push(`Sắp hết hạn trong ${diff} ngày`);
      if (diff < 0) w.push('Đã hết hạn - Hợp đồng nền đã hết hiệu lực, cần rà soát hạn mức');
    }

    const seasonRisk = contract.seasonIds?.some((sid) => {
      const s = seasons.find((se) => se.id === sid);
      return s?.status === 'Có rủi ro';
    });
    if (seasonRisk) w.push('Mùa vụ liên quan có rủi ro');

    const lowHarvest = relatedHarvests.some((h) => h.status === 'Sản lượng thấp');
    if (lowHarvest) w.push('Sản lượng thực tế thấp hơn dự kiến');

    return w;
  }, [contract, seasons, relatedHarvests]);

  // Related seasons detail
  const relatedSeasonsDetail = useMemo(() => {
    return (contract.seasonIds || [])
      .map((sid) => seasons.find((s) => s.id === sid))
      .filter(Boolean) as Season[];
  }, [contract.seasonIds, seasons]);

  const kycLabels: Record<string, string> = {
    ekyc_verified: 'Đã xác thực',
    ekyc_pending: 'Đang xác thực',
    ekyc_need_more: 'Cần bổ sung',
    ekyc_failed: 'Thất bại',
    registered: 'Chưa eKYC',
  };

  const kycColors: Record<string, string> = {
    ekyc_verified: 'bg-agrida-50 text-agrida-700',
    ekyc_pending: 'bg-sky-50 text-sky-700',
    ekyc_need_more: 'bg-amber-50 text-amber-700',
    ekyc_failed: 'bg-red-50 text-red-700',
    registered: 'bg-slate-50 text-slate-500',
  };

  const riskRateTable = getRiskRatioTable();

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-4xl max-h-[100dvh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-modal-slide-up">
        {/* Header */}
        <div className="shrink-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">{contract.soHopDong}</h3>
            <StatusBadge status={contract.status} />
            {isBaoTieu && (
              <span className="hidden sm:inline text-xs px-2 py-0.5 rounded-full bg-agrida-50 text-agrida-700 border border-agrida-200 whitespace-nowrap">Bao tiêu</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <button type="button" onClick={onEdit} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-agrida-50 text-agrida-700" title="Sửa">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line" /></span>
              </button>
            )}
            <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500" title="Đóng">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line" /></span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 border-b border-slate-200 px-4 sm:px-6 overflow-x-auto">
          <div className="flex items-center gap-0 min-w-max">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-agrida-600 text-agrida-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={tab.icon} /></span>
                {tab.label}
                {tab.key === 'docs' && docCount > 0 && (
                  <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{docCount}</span>
                )}
                {tab.key === 'loans' && relatedLoans.length > 0 && (
                  <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{relatedLoans.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {/* ---- Warnings (always visible) ---- */}
          {warnings.length > 0 && (
            <div className="space-y-1.5">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0"><i className="ri-error-warning-line" /></span>
                  {w}
                </div>
              ))}
            </div>
          )}

          {/* ---- Farmer + eKYC summary (always visible) ---- */}
          <div className="bg-slate-50 rounded-xl border border-slate-100 p-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 flex items-center justify-center text-slate-400"><i className="ri-user-line" /></span>
              <span className="text-xs font-medium text-slate-700">{farmerName}</span>
            </div>
            <span className="text-slate-300">|</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${kycColors[farmerKyc.kycStatus] || 'bg-slate-50 text-slate-500'}`}>
              eKYC: {kycLabels[farmerKyc.kycStatus] || farmerKyc.kycStatus}
            </span>
            {farmerKyc.creditScore !== undefined && (
              <>
                <span className="text-slate-300">|</span>
                <span className={`text-xs font-semibold ${farmerKyc.creditScore >= 700 ? 'text-agrida-600' : farmerKyc.creditScore >= 550 ? 'text-amber-600' : 'text-red-600'}`}>
                  Score: {farmerKyc.creditScore}
                </span>
              </>
            )}
            {farmerKyc.riskLevel && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-xs font-medium text-slate-600">Risk: {farmerKyc.riskLevel}</span>
              </>
            )}
          </div>

          {/* ---- TAB: Thông tin ---- */}
          {activeTab === 'info' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex justify-between sm:block"><span className="text-slate-400">Loại hợp đồng</span><span className="text-slate-700 font-medium">{contract.loaiHopDong || '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Đối tác / Nông hộ</span><span className="text-slate-700 font-medium">{contract.doiTac || farmerName}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Ngày ký</span><span className="text-slate-700 font-medium">{formatDate(contract.ngayKy)}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Ngày hiệu lực</span><span className="text-slate-700 font-medium">{formatDate(contract.ngayHieuLuc || '') || '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Ngày hết hạn</span><span className="text-slate-700 font-medium">{formatDate(contract.ngayHetHan)}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Giá trị hợp đồng</span><span className="text-slate-700 font-semibold">{formatVND(contract.giaTri)}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Cây trồng</span><span className="text-slate-700 font-medium">{contract.cayTrong || '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Giống</span><span className="text-slate-700 font-medium">{contract.giong || '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Diện tích</span><span className="text-slate-700 font-medium">{contract.dienTich ? `${contract.dienTich} ha` : '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Sản lượng cam kết</span><span className="text-slate-700 font-medium">{contract.sanLuongDuKien ? `${formatNumber(contract.sanLuongDuKien)} kg` : '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Đơn giá dự kiến</span><span className="text-slate-700 font-medium">{contract.donGiaDuKien ? `${formatVND(contract.donGiaDuKien)}/kg` : '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Ngày giao hàng dự kiến</span><span className="text-slate-700 font-medium">{contract.ngayGiaoHangDuKien ? formatDate(contract.ngayGiaoHangDuKien) : '-'}</span></div>
                <div className="flex justify-between sm:block"><span className="text-slate-400">Trạng thái</span><StatusBadge status={contract.status} /></div>
              </div>

              {contract.cropLines && contract.cropLines.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-1.5">Cây trồng theo hợp đồng ({contract.cropLines.length})</h4>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-2.5 py-1.5 text-left font-medium">Cây trồng</th>
                          <th className="px-2.5 py-1.5 text-right font-medium">Diện tích</th>
                          <th className="px-2.5 py-1.5 text-right font-medium">Sản lượng</th>
                          <th className="px-2.5 py-1.5 text-right font-medium">Đơn giá</th>
                          <th className="px-2.5 py-1.5 text-right font-medium">Thành tiền</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contract.cropLines.map((line, i) => (
                          <tr key={i} className="border-t border-slate-100 text-slate-700">
                            <td className="px-2.5 py-1.5 font-medium">{line.crop}</td>
                            <td className="px-2.5 py-1.5 text-right">{line.areaHa !== undefined ? `${formatNumber(line.areaHa)} ha` : '-'}</td>
                            <td className="px-2.5 py-1.5 text-right">{line.expectedOutputKg !== undefined ? `${formatNumber(line.expectedOutputKg)} kg` : '-'}</td>
                            <td className="px-2.5 py-1.5 text-right">{line.unitPriceVnd !== undefined ? `${formatVND(line.unitPriceVnd)}/kg` : '-'}</td>
                            <td className="px-2.5 py-1.5 text-right">
                              {line.expectedOutputKg !== undefined && line.unitPriceVnd !== undefined ? formatVND(line.expectedOutputKg * line.unitPriceVnd) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Nội dung */}
              <div>
                <h4 className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-article-line" /></span>
                  Nội dung hợp đồng
                </h4>
                <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3 leading-relaxed">{contract.noiDung}</p>
              </div>

              {/* Ghi chú */}
              {contract.ghiChu && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-sticky-note-line" /></span>
                    Ghi chú
                  </h4>
                  <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3">{contract.ghiChu}</p>
                </div>
              )}
            </div>
          )}

          {/* ---- TAB: Mùa vụ ---- */}
          {activeTab === 'seasons' && (
            <div className="space-y-3">
              {relatedSeasonsDetail.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-100 mb-2"><i className="ri-seedling-line text-lg" /></span>
                  <p className="text-sm">Chưa có mùa vụ liên quan</p>
                </div>
              ) : (
                relatedSeasonsDetail.map((season) => {
                  const field = season.fieldId ? { name: season.fieldId } : null;
                  const harvestCount = relatedHarvests.filter((h) => h.seasonId === season.id).length;
                  return (
                    <div key={season.id} className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{season.name}</span>
                          <span className="text-[11px] text-slate-400">{season.maMuaVu}</span>
                          <StatusBadge status={season.status} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5 text-xs">
                        <div><span className="text-slate-400">Cây trồng:</span> <span className="text-slate-700">{season.cayTrong}</span></div>
                        <div><span className="text-slate-400">Giống:</span> <span className="text-slate-700">{season.variety}</span></div>
                        <div><span className="text-slate-400">Sản lượng DK:</span> <span className="text-slate-700">{formatNumber(season.expectedYield)} {season.unit || 'kg'}</span></div>
                        <div><span className="text-slate-400">Ngày bắt đầu:</span> <span className="text-slate-700">{formatDate(season.startDate)}</span></div>
                        <div><span className="text-slate-400">Ngày kết thúc:</span> <span className="text-slate-700">{season.endDate ? formatDate(season.endDate) : 'Chưa có'}</span></div>
                        <div><span className="text-slate-400">Thu hoạch:</span> <span className="text-slate-700">{harvestCount} lần</span></div>
                      </div>
                    </div>
                  );
                })
              )}

              {/* Harvests summary */}
              {relatedHarvests.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-shopping-basket-line" /></span>
                    Thu hoạch liên quan ({relatedHarvests.length})
                  </h4>
                  <div className="space-y-2">
                    {relatedHarvests.map((h) => (
                      <div key={h.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-2.5 text-xs">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={h.status} />
                          <span className="font-medium text-slate-700">{h.maLanThuHoach}</span>
                          <span className="text-slate-400">{h.cayTrong}</span>
                        </div>
                        <div className="flex items-center gap-3 text-slate-500">
                          <span>SL: {formatNumber(h.soLuong)} {h.donVi}</span>
                          {h.actualYieldConfirmed && (
                            <span className="text-agrida-600 font-medium">XN: {formatNumber(h.actualYieldConfirmed)} {h.donVi}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- TAB: Hạn mức ---- */}
          {activeTab === 'limits' && (
            <div className="space-y-4">
              {/* Overview card */}
              {isBaoTieu && (
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                  <h4 className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-bank-card-line" /></span>
                    Tổng quan hạn mức từ hợp đồng này
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Giá trị HĐ</p>
                      <p className="text-sm font-bold text-slate-900">{formatVND(contract.giaTri)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Đã cấp</p>
                      <p className="text-sm font-bold text-amber-700">{formatVND(hanMucDaCap)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Còn có thể cấp</p>
                      <p className={`text-sm font-bold ${hanMucConLai > 0 ? 'text-agrida-700' : 'text-slate-400'}`}>{formatVND(hanMucConLai)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Tỷ lệ đã dùng</p>
                      <div className="mt-1">
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${usedPercent > 80 ? 'bg-red-500' : usedPercent > 50 ? 'bg-amber-500' : 'bg-agrida-500'}`} style={{ width: `${Math.min(usedPercent, 100)}%` }} />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{usedPercent}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Limit proposal */}
              {isBaoTieu && proposal && (
                <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-sky-800 mb-2 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calculator-line" /></span>
                    Đề xuất hạn mức từ hệ thống
                  </h4>
                  <p className="text-[10px] text-sky-600 mb-2">{proposal.description}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="text-sky-500">Risk:</span>
                      <span className="ml-1 font-semibold text-sky-800">{proposal.riskLevel}</span>
                    </div>
                    <div>
                      <span className="text-sky-500">Tỷ lệ:</span>
                      <span className="ml-1 font-semibold text-sky-800">{proposal.ratioPercent}</span>
                    </div>
                    <div>
                      <span className="text-sky-500">Đề xuất:</span>
                      <span className="ml-1 font-bold text-agrida-700">{formatVND(proposal.suggestedLimit)}</span>
                    </div>
                  </div>

                  {/* Risk ratio table */}
                  <div className="mt-3 pt-3 border-t border-sky-200">
                    <p className="text-[10px] text-sky-500 mb-1.5">Bảng tỷ lệ đề xuất theo risk:</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                      {riskRateTable.map((r) => (
                        <div key={r.risk} className={`text-center px-2 py-1 rounded text-[10px] ${r.risk === proposal.riskLevel ? 'bg-sky-200 font-bold' : 'bg-white'} ${r.color}`}>
                          <span className="block">{r.risk}</span>
                          <span className="block font-semibold">{r.ratio}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Related limits — the real backend keeps one running limit per farmer
                  (not per-contract); this shows the farmer's current limit record. */}
              {relatedLimits.length > 0 ? (
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-funds-line" /></span>
                    Hạn mức hiện tại của nông hộ
                  </h4>
                  <div className="space-y-2">
                    {relatedLimits.map((lm, idx) => (
                      <div key={lm.id} className="bg-white border border-slate-200 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-agrida-100 text-agrida-600 flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>
                            <span className="text-xs font-medium text-slate-700">{lm.loaiDinhMuc}</span>
                            <span className="text-[10px] text-slate-400">Năm {lm.nam}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs">
                          <div><span className="text-slate-400">Hạn mức: </span><span className="font-semibold text-slate-700">{formatVND(lm.hanMuc)}</span></div>
                          <div><span className="text-slate-400">Đã dùng: </span><span className="font-semibold text-amber-700">{formatVND(lm.daSuDungThucTe ?? lm.daSuDung)}</span></div>
                          {/* Feedback 08 A1: "Đã dùng" alone used to silently include
                              the locked amount too — shown as its own figure now. */}
                          {!!lm.dangKhoa && <div><span className="text-slate-400">Đang khóa: </span><span className="font-semibold text-sky-700">{formatVND(lm.dangKhoa)}</span></div>}
                          <div><span className="text-slate-400">Còn: </span><span className={`font-semibold ${lm.hanMuc - lm.daSuDung > 0 ? 'text-agrida-700' : 'text-red-600'}`}>{formatVND(lm.hanMuc - lm.daSuDung)}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-100 mb-2"><i className="ri-funds-line text-lg" /></span>
                  <p className="text-sm">Nông hộ chưa được cấp hạn mức</p>
                </div>
              )}
            </div>
          )}

          {/* ---- TAB: Khoản vay ---- */}
          {activeTab === 'loans' && (
            <div className="space-y-3">
              {relatedLoans.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-100 mb-2"><i className="ri-money-cny-circle-line text-lg" /></span>
                  <p className="text-sm">Chưa có khoản vay liên quan</p>
                </div>
              ) : (
                relatedLoans.map((loan) => {
                  const season = seasons.find((s) => s.id === loan.seasonId);
                  return (
                    <div key={loan.id} className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${loan.loai === 'Tiền' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-agrida-50 text-agrida-700 border-agrida-200'}`}>
                            {loan.loai}
                          </span>
                          <StatusBadge status={loan.status} />
                        </div>
                        {loan.loai === 'Tiền' && (
                          <span className="text-sm font-bold text-slate-900">{formatVND(loan.soTien)}</span>
                        )}
                        {loan.loai === 'Vật tư' && (
                          <span className="text-xs text-slate-600">{loan.soLuongVatTu} {loan.donViVatTu}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-[10px] text-slate-500">
                        <span>Mục đích: {loan.mucDich}</span>
                        {season && <span>Mùa vụ: {season.name}</span>}
                        <span>Ngày yêu cầu: {formatDate(loan.ngayYeuCau)}</span>
                        {loan.ngayDuyet && <span>Ngày duyệt: {formatDate(loan.ngayDuyet)}</span>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ---- TAB: Tài liệu ---- */}
          {activeTab === 'docs' && (
            <div className="space-y-3">
              {contract.attachments && contract.attachments.length > 0 ? (
                contract.attachments.map((att) => (
                  <div key={att.fileId} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center text-slate-500">
                        <i className={getFileIcon(att.type)} />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{att.caption || (att.type === 'image' ? 'Ảnh đính kèm' : 'Tài liệu')}</p>
                    </div>
                    <button
                      type="button"
                      disabled={!docUrls[att.fileId]}
                      onClick={() => window.open(docUrls[att.fileId], '_blank', 'noopener,noreferrer')}
                      className="px-3 py-1.5 rounded-md text-xs font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 disabled:opacity-50 transition-colors flex items-center gap-1 whitespace-nowrap"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line" /></span>
                      Xem
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-amber-50 mb-2"><i className="ri-file-warning-line text-lg text-amber-500" /></span>
                  <p className="text-sm font-medium text-amber-700">Chưa có tài liệu đính kèm</p>
                  <p className="text-xs text-amber-500 mt-1">Vui lòng bổ sung tài liệu hợp đồng</p>
                </div>
              )}
            </div>
          )}

          {/* ---- TAB: Lịch sử ---- */}
          {activeTab === 'history' && (
            <div className="space-y-3">
              {editHistories.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-100 mb-2"><i className="ri-history-line text-lg" /></span>
                  <p className="text-sm">Chưa có lịch sử chỉnh sửa</p>
                </div>
              ) : (
                editHistories.map((eh) => (
                  <div key={eh.id} className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-history-line" /></span>
                      <span>{formatDateTime(eh.ngaySua)}</span>
                      <span>·</span>
                      <span className="text-slate-600">{eh.nguoiSua}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-slate-700">{eh.truong}:</span>
                      <span className="text-red-500 line-through">{eh.giaTriCu}</span>
                      <span className="w-3 h-3 flex items-center justify-center text-slate-400"><i className="ri-arrow-right-line" /></span>
                      <span className="text-agrida-600 font-medium">{eh.giaTriMoi}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-white border-t border-slate-200 px-4 sm:px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {onAddDocs && (
              <button
                type="button"
                onClick={onAddDocs}
                className="px-3 py-2 rounded-md text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors flex items-center gap-1.5 whitespace-nowrap min-h-[40px]"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-add-line" /></span>
                Bổ sung tài liệu
              </button>
            )}
            {onExtend && (
              <button
                type="button"
                onClick={onExtend}
                className="px-3 py-2 rounded-md text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center gap-1.5 whitespace-nowrap min-h-[40px]"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calendar-check-line" /></span>
                Gia hạn
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors whitespace-nowrap min-h-[44px]"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
